import _ from 'underscore';
import Promise from 'bluebird';
import { useState, useCallback, useEffect, useContext } from 'react';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { ScopeContext } from '@bingads-webui-react/scope-service';

const odata = DependencyResolver.get('component/http/channel/odata');

const odataCallerFormatter = targets => (
  {
    host: 'adinsight.odata.url',
    path: 'AdInsight/V2/',
    data: { request: targets },
    customHeader: {
      name: 'x-ms-scenarioname',
      value: 'DisplayPlusCampaign',
    },
  }
);

const GenderTypeCount = 3;
const AgeTypeCount = 6;

const getEstimation = _.memoize((url, payload) => odata.post(url, payload), (url, payload) => `${url} - ${JSON.stringify(payload)}`);

export const useTargetingEstimationCaller = ({
  // campaignSubTypes being plumbed in for when API supports multiple types
  targetsForAudienceSizeEstimation, targetsForAudienceKPIEstimation, showBreakdown, interestOnlyTargets, otherAudienceOnlyTargets, audienceUsePrivacyCheck, campaignSubTypes, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const urlBase = `Customers(${cid})/Accounts(${aid})/`;
  const audienceEstimationUrl = `${urlBase}Default.AudienceKPIEstimationV2`;
  const audienceSizeEstimationUrl = `${urlBase}Default.AudienceEstimationV2`;
  const privacyCheckUrl = `${urlBase}Default.PrivacyCheckV3`;

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isImpressionTooSpecific, setIsImpressionTooSpecific] = useState(false);
  const [privacyResult, setPrivacyResult] = useState(false);
  const [audienceSize, setAudienceSize] = useState({ Unit: '', Value: 0 });
  const [ageOnlyEstimation, setAgeOnlyEstimation] = useState(null);
  const [genderOnlyEstimation, setGenderOnlyEstimation] = useState(null);
  const [locationOnlyEstimation, setLocationOnlyEstimation] = useState(null);
  const [audienceOnlyEstimation, setAudienceOnlyEstimation] = useState(null);
  const [interestOnlyEstimation, setInterestOnlyEstimation] = useState(null);

  const defaultPrivacyCheckResult = { CheckResult: '' };

  const getAudienceReachEstimation = useCallback(() => {
    if (_.isEmpty(targetsForAudienceSizeEstimation)) {
      return setData({
        EstAudienceSize: {
          Value: 0,
        },
      });
    }

    setIsLoading(true);
    const getAgeOnlyEstimation = showBreakdown && !_.isEmpty(targetsForAudienceSizeEstimation.Age.Includes) && (targetsForAudienceSizeEstimation.Age.Includes.length !== AgeTypeCount);
    const getGenderOnlyEstimation = showBreakdown && !_.isEmpty(targetsForAudienceKPIEstimation.Gender.Includes) && (targetsForAudienceKPIEstimation.Gender.Includes.length !== GenderTypeCount);
    const getLocationOnlyEstimation = showBreakdown && (!_.isEmpty(targetsForAudienceSizeEstimation.Location.Includes)
      || !_.isEmpty(targetsForAudienceSizeEstimation.Location.Excludes)
      || !_.isEmpty(targetsForAudienceKPIEstimation.RadiusTargets)
    );
    const getAudienceOnlyEstimation = showBreakdown && (!_.isEmpty(otherAudienceOnlyTargets.Audience.Includes) || !_.isEmpty(otherAudienceOnlyTargets.Audience.Excludes));
    const getInterestOnlyEstimation = showBreakdown && (!_.isEmpty(interestOnlyTargets.Audience.Includes) || !_.isEmpty(interestOnlyTargets.Audience.Excludes));

    return Promise.props({
      audienceEstimation: getEstimation(audienceEstimationUrl, odataCallerFormatter(targetsForAudienceKPIEstimation)),
      audienceSizeEstimation: getEstimation(audienceSizeEstimationUrl, odataCallerFormatter(targetsForAudienceSizeEstimation)),
      audiencePrivacyCheck: audienceUsePrivacyCheck ? getEstimation(privacyCheckUrl, odataCallerFormatter(targetsForAudienceSizeEstimation)) : defaultPrivacyCheckResult,
      ageOnly: getAgeOnlyEstimation ? getEstimation(audienceSizeEstimationUrl, odataCallerFormatter(_.pick(targetsForAudienceSizeEstimation, ['Age', 'AutoTargeting', 'CampaignBiddingStrategy', 'CampaignSubType']))) : Promise.resolve(null),
      genderOnly: getGenderOnlyEstimation ? getEstimation(audienceSizeEstimationUrl, odataCallerFormatter((_.pick(targetsForAudienceSizeEstimation, ['Gender', 'AutoTargeting', 'CampaignBiddingStrategy', 'CampaignSubType'])))) : Promise.resolve(null),
      locationOnly: getLocationOnlyEstimation ? getEstimation(audienceSizeEstimationUrl, odataCallerFormatter((_.pick(targetsForAudienceSizeEstimation, ['Location', 'RadiusTargets', 'AutoTargeting', 'CampaignBiddingStrategy', 'CampaignSubType'])))) : Promise.resolve(null),
      audienceOnly: getAudienceOnlyEstimation ? getEstimation(audienceSizeEstimationUrl, odataCallerFormatter((_.pick(otherAudienceOnlyTargets, ['Audience', 'AutoTargeting', 'CampaignBiddingStrategy', 'CampaignSubType'])))) : Promise.resolve(null),
      interestOnly: getInterestOnlyEstimation ? getEstimation(audienceSizeEstimationUrl, odataCallerFormatter((_.pick(interestOnlyTargets, ['Audience', 'AutoTargeting', 'CampaignBiddingStrategy', 'CampaignSubType'])))) : Promise.resolve(null),
    })
      .then(({
        audienceEstimation, audienceSizeEstimation, audiencePrivacyCheck, ageOnly, genderOnly, locationOnly, audienceOnly, interestOnly,
      }) => {
        setData(audienceEstimation);
        setIsImpressionTooSpecific(audienceSizeEstimation && audienceSizeEstimation.IsImpressionTooSpecific);
        setPrivacyResult(_.result(audiencePrivacyCheck, 'CheckResult'));
        setAudienceSize(audienceSizeEstimation);
        setAgeOnlyEstimation(ageOnly);
        setGenderOnlyEstimation(genderOnly);
        setLocationOnlyEstimation(locationOnly);
        setAudienceOnlyEstimation(audienceOnly);
        setInterestOnlyEstimation(interestOnly);
      })
      .catch(err => Promise.reject(err))
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    targetsForAudienceSizeEstimation,
    audienceEstimationUrl,
    targetsForAudienceKPIEstimation,
    audienceSizeEstimationUrl,
    privacyCheckUrl,
    showBreakdown,
    otherAudienceOnlyTargets,
    interestOnlyTargets,
  ]);

  useEffect(() => {
    if (targetsForAudienceSizeEstimation) {
      getAudienceReachEstimation();
    }
  }, [getAudienceReachEstimation, targetsForAudienceSizeEstimation]);

  return [
    isLoading,
    data,
    isImpressionTooSpecific,
    privacyResult,
    audienceSize,
    ageOnlyEstimation,
    genderOnlyEstimation,
    locationOnlyEstimation,
    audienceOnlyEstimation,
    interestOnlyEstimation,
  ];
};
